'use client';

import { cn } from '@core/utils';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { UnauthQueryOptions } from '@/api';
import { PhotoReviewSwiper } from '@/app/donation/_components/PhotoReviewSwiper';
import { GIFT_REVIEW_SORT_TYPE } from '@/app/donation/constants';
import type { GiftReviewSortType } from '@/app/donation/types';

import { TABS } from '../../../../constants';
import type { GiftDetailPageProps } from '../../../../page';
import { ResponsiveDivider } from '../../../GiftDetailTemplate/ResponsiveDivider';
import { ReviewSummary } from '../../../ReviewSummary';
import { ReviewList } from './_components/ReviewList';

interface GiftTabContentReviewProps {
  reviewRating?: number;
  reviewCount: number;
  productNumber: string;
  searchParams?: GiftDetailPageProps['searchParams'];
}

export const GiftTabContentReview = ({
  reviewRating,
  reviewCount,
  productNumber,
}: GiftTabContentReviewProps) => {
  const [reviewSort, setReviewSort] = useState<GiftReviewSortType>(
    GIFT_REVIEW_SORT_TYPE.LATEST,
  );

  //* 포토후기 목록
  const { data: photoReviewRes } = useSuspenseQuery({
    ...UnauthQueryOptions['get:/wello/v2/allows/home/gift-review/photo']({
      query: {
        productNumber,
        page: 1,
        size: 10,
      },
    }),
  });

  //* 후기 목록
  const { data: reviewRes } = useQuery({
    ...UnauthQueryOptions['get:/wello/v2/allows/home/gift-review']({
      query: {
        productNumber,
        sortType: reviewSort,
        page: 1,
        size: 20,
      },
    }),
  });

  const photoReviewList = photoReviewRes?.context?.contents ?? [];
  const photoReviewCount = photoReviewRes?.context?.totalCount ?? 0;
  const reviewList = reviewRes?.context?.contents ?? [];
  const reviewTotalCount = reviewRes?.context?.totalCount ?? 0;

  return (
    <section
      className={cn('flex flex-col pt-16', 'mobile-tablet:pt-32')}
      id={TABS.REVIEW}
    >
      <ReviewSummary
        reviewRating={reviewRating}
        reviewCount={reviewCount}
        className="mobile-tablet:mx-20"
      />
      <PhotoReviewSwiper
        photoReviewList={photoReviewList.map(({ fileUrl: file_url, reviewId: review_id }) => ({
          fileUrl: file_url,
          reviewId: review_id,
        }))}
        photoReviewCount={photoReviewCount}
        productNumber={productNumber}
        className="mobile-tablet:px-20"
      />
      <ResponsiveDivider className="h-0" />
      <div className="flex flex-col">
        <div className="flex items-center justify-end gap-8 border-b border-Gray-200 px-12 py-16">
          <button
            type="button"
            onClick={() => setReviewSort(GIFT_REVIEW_SORT_TYPE.LATEST)}
            className={cn('font-700 text-Gray-500 typo-body4', {
              'text-Gray-600': reviewSort !== GIFT_REVIEW_SORT_TYPE.RATING_DESC,
            })}
          >
            최신순
          </button>
          <div className="h-16 w-1 bg-Gray-300" />
          <button
            type="button"
            onClick={() => setReviewSort(GIFT_REVIEW_SORT_TYPE.RATING_DESC)}
            className={cn('font-700 text-Gray-500 typo-body4', {
              'text-Gray-600': reviewSort === GIFT_REVIEW_SORT_TYPE.RATING_DESC,
            })}
          >
            평점 높은순
          </button>
        </div>
        <ReviewList
          initialList={reviewList}
          totalCount={reviewTotalCount}
          sortType={reviewSort}
          productNumber={productNumber}
        />
      </div>
    </section>
  );
};
